/* eslint-disable id-length */
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import _map from 'lodash/map';
import _last from 'lodash/last';
import _pick from 'lodash/pick';
import _groupBy from 'lodash/groupBy';
import _isString from 'lodash/isString';
import _isArray from 'lodash/isArray';

/* eslint-disable no-restricted-syntax */
export const groupModules = (arr) => {
  return arr.reduce((acc, item) => {
    const lastGroupIndex = acc.length - 1;

    if (lastGroupIndex >= 0 && item.type in acc[lastGroupIndex]) {
      acc[lastGroupIndex][item.type].push(item);
    } else {
      acc.push({ [item.type]: [item] });
    }
    return acc;
  }, []);
};

const findSegment = (product, segments) => {
  const [row, column] = product.Origin;
  const [rowSpan, columnSpan] = product.metadata.Span;

  // CODE SMELL: TOO HEAVYWEIGHT
  for (const segment of segments) {
    const [startRow, startColumn] = segment.Start;
    const [endRow, endColumn] = segment.End;

    if (
      row >= startRow
      && row + rowSpan - 1 <= endRow
      && column >= startColumn
      && column + columnSpan - 1 <= endColumn
    ) {
      return segment;
    }
  }
  return null; // if the product is not in any segment
};

export const getSpan = (value, excludeTubes) => {
  if (!value) return [0, 0, 0];
  const span = [...value?.split(',')].map(Number);
  return excludeTubes ? span?.slice(0, -1) : span;
};

function getSize({ Start, End }) {
  const rows = End[0] - Start[0] + 1;
  const cols = End[1] - Start[1] + 1;
  return `${rows}-${cols}`;
}

export const groupItemsBySegment = ({ Products, Metadata, Matrix }) => {
  const productsBySegment = {};

  // CODE SMELL: TOO HEAVYWEIGHT
  for (const product of Products) {
    const sku = product.Sku;
    const metadata = Metadata[sku];
    const productWithMetadata = { ...product, metadata };
    const segment = findSegment(productWithMetadata, Matrix?.Segments);

    if (segment) {
      const segmentKey = JSON.stringify(segment);
      if (!productsBySegment[segmentKey]) {
        const size = getSize(segment);
        productsBySegment[segmentKey] = { size, ...segment, Items: [] };
      }
      productsBySegment[segmentKey].Items.push(productWithMetadata);
    }
  }

  return Object.values(productsBySegment);
};

export const getSideName = (origin) => {
  const [, col] = origin;
  return col >= 2 ? 'right' : 'left';
};

export const getTypeNameBySpan = (span) => {
  let key;
  if (_isString(span)) {
    const [row,, col] = span;
    key = `${row}x${col}`;
  }

  if (_isArray(span)) {
    const [row, col] = span;
    key = `${row}x${col}`;
  }

  const typeLookup = {
    '1x1': 'unique',
    '2x2': 'narrow',
    '2x4': 'wide',
    '4x4': 'full',
  };

  return typeLookup[key] || 'default';
};

export const getTypeName = (metadata) => {
  if (!metadata) {
    return 'default';
  }
  const [row, col] = getSpan(metadata?.metadata?.GridSpan);
  const { ProductType: productType } = metadata?.metadata;
  if (productType === 'Base') return 'Base';
  //  future reference should callout that this doesn't support Top => Full
  if (productType === 'Core' && row === 4 && col === 4) return 'full';

  const key = [row, col].join('x');
  const typeLookup = {
    '1x1': 'unique',
    '2x2': 'narrow',
    '2x4': 'wide',
    '4x4': 'full',
  };

  return typeLookup[key] || 'default';
};

export const getHeightEquality = (itemsGroups) => {
  const leftHeight = itemsGroups.left
    ? itemsGroups.left.reduce((acc, item) => acc + item.Origin[2], 0)
    : 0;
  const rightHeight = itemsGroups.right
    ? itemsGroups.right.reduce((acc, item) => acc + item.Origin[2], 0)
    : 0;

  return leftHeight === rightHeight;
};

export const getOriginsForWide = (stackType, stack, origins, areHeightsEqual) => {
  // incoming product.type === wide
  let newOrigins = origins;

  switch (stackType) {
  case 'narrow':
    if (areHeightsEqual) {
      Object.entries(stack).forEach(([side, items]) => {
        for (let length = items.length - 1; length >= 0; length -= 1) {
          const gridSpan = getSpan(items?.[length]?.metadata?.GridSpan);
          const topOnly = gridSpan[2] === -1;
          if (topOnly) {
            const mOrigin = items[length].Origin.slice(0, -1);
            const wideIndex = newOrigins.findIndex((itemOrigin) => _isEqual(mOrigin, itemOrigin.origin));
            if (wideIndex > -1) {
              newOrigins.splice(wideIndex, 1);
            }
          }
        }
      });
    } else {
      return [];
    }
    break;
  case 'wide': {
    const topStackItem = _last(stack);
    const gridSpan = getSpan(topStackItem?.metadata?.GridSpan?.value);
    const topOnly = gridSpan[2] === -1;
    if (topOnly) {
      newOrigins = [];
    }

    return newOrigins;
  }
  default:
    return [];
  }

  return newOrigins;
};

export const getOrigins = (stackType, stack, origins, productType) => {
  let newOrigins = origins;

  const itemGroups = _groupBy(stack, (item) => {
    return getSideName(item.Origin);
  });

  const areHeightsEqual = getHeightEquality(itemGroups);

  const handleTopOnly = () => Object.entries(itemGroups).forEach(([side, items]) => {
    for (let length = items.length - 1; length >= 0; length -= 1) {
      const gridSpan = getSpan(items[length]?.metadata.GridSpan);
      const topOnly = gridSpan[2] === -1;
      if (topOnly) {
        const mOrigin = items[length].Origin.slice(0, -1);
        const index = newOrigins.findIndex((itemOrigin) => _isEqual(mOrigin, itemOrigin.origin));
        if (index > -1) {
          newOrigins[index] = { ...newOrigins[index], visible: 'hidden' };
        }
      }
    }
  });

  switch (stackType) {
  case 'narrow':
    if (productType === 'wide') {
      if (!areHeightsEqual) {
        newOrigins = [];
      }
      handleTopOnly(stackType);
    } else {
      handleTopOnly(stackType);
    }
    break;
  case 'wide': {
    const topStackItem = _last(stack);
    const gridSpan = getSpan(topStackItem?.metadata.GridSpan);
    const topOnly = gridSpan[2] === -1;
    if (topOnly) {
      newOrigins = [];
    }
    return newOrigins;
  }

  case 'full': {
    return newOrigins;
  }

  default:
    return [];
  }

  return newOrigins;
};

export const splitIntoGroups = (products, metadata, matrix) => {
  const multiStackGroups = {
    front: { items: [], children: [] },
    rear: { items: [], children: [] },
    single: { items: [], children: [] },
    full: { items: [], children: [] },
    Base: { items: [] },
  };

  if (_isEmpty(products)) return multiStackGroups;

  const base = products.find((product) => product?.type === 'Base');
  const baseMetadata = base && metadata?.[base?.choiceId]?.metadata;

  const baseSpan = baseMetadata
    ? getSpan(baseMetadata?.GridSpan, true)
    : [];
  const matrixSpan = Object.values(_pick(matrix, ['rows', 'columns']));
  const full = products.find((product) => product?.type === 'full');
  // const fullIndex = products.findIndex((product) => product?.type === 'full');
  const fullMetadata = full && metadata?.[full?.choiceId]?.metadata;
  const fullSpan = fullMetadata
    ? getSpan(fullMetadata?.GridSpan, true)
    : [];

  // const isMultiStack = (baseMetadata && _isEqual(baseSpan, matrixSpan));
  const isMultiStack = (baseMetadata && _isEqual(baseSpan, matrixSpan))
  || (fullMetadata && _isEqual(fullSpan, matrixSpan));
  if (base) {
    const { choiceId } = base;
    const itemType = {
      ...base,
      metadata: metadata[choiceId],
      type: getTypeName(metadata?.[choiceId]),
    };

    multiStackGroups.Base.items.push(itemType);
  }

  _map(products, (product) => {
    if (base && product.Sku === base.Sku) return;

    const { Origin: origin, choiceId } = product;

    if (!metadata?.[choiceId]?.metadata) return;

    const {
      GridSpan: gridSpan,
      ProductType: productType
    } = metadata?.[choiceId]?.metadata;
    const type = getTypeNameBySpan(getSpan(gridSpan));
    const itemType = {
      ...product,
      metadata: metadata[choiceId].metadata,
      type,
    };

    const span = gridSpan.split(',');
    const fullItems = `${span[0]},${span[1]}` === '4,4'; // 4x4 is full item
    let location;
    if (isMultiStack) {
      location = 'full';
    } else {
      location = fullItems ? 'full' : 'single';
    }
    multiStackGroups[location].items.push(itemType);
  });

  return multiStackGroups;
};

export const isSelfProductType = (multiStackSegmentItems, selectedProductGridSpan) => {
  if (_isEmpty(multiStackSegmentItems) || !selectedProductGridSpan) {
    return false;
  }
  const {
    ProductType,
    GridSpan
  } = _last(multiStackSegmentItems)?.metadata;
  const lastItemInStackSpan = getSpan(GridSpan, true).toString();
  const selectedProductSpan = getSpan(selectedProductGridSpan, true).toString();
  return ProductType === 'Self' && (lastItemInStackSpan !== selectedProductSpan);
};

/**
 * Determines if an item is draggable based on its metadata and the context of other items.
 *
 * @param {Object} baseItem - The base item being checked for drag eligibility.
 * @param {Object} targetItem - The target item that the base item is being dragged onto.
 * @param {Object} aboveTargetItem - The item above the target item in the stack.
 * @param {Array<Object>} fullStackItems - The full stack of items being considered.
 * @returns {boolean} - Returns `true` if the item is draggable, otherwise `false`.
 */
export const isDraggableItemWithInclusiveChoices = (baseItem, targetItem, aboveTargetItem, fullStackItems) => {
  // When TargetItem is directly on top of baseItem check if inclusive choices apply
  if (baseItem?.metadata?.metadata?.InclusiveChoices
    && fullStackItems[0].stackItemId === targetItem.stackItemId
    && (targetItem && aboveTargetItem)
    && baseItem.metadata.metadata.InclusiveChoices?.includes(String(targetItem.choiceId))
    && !baseItem.metadata.metadata.InclusiveChoices?.includes(String(aboveTargetItem.choiceId))) {
    return false;
  }
  // If the targetItem is the last item in the stack, it is draggable
  if (fullStackItems[fullStackItems.length - 1].stackItemId === targetItem.stackItemId) {
    return true;
  }
  // If the targetItem is not the last item in the stack, check if it has inclusive choices
  if (targetItem.metadata?.InclusiveChoices) {
    return false;
  }
  return true;
};

const QUADRANTS = {
  RL: { x: 0, y: 0 },
  FL: { x: 0, y: 2 },
  RR: { x: 2, y: 0 },
  FR: { x: 2, y: 2 },
};

export const parseGridSpan = (gridSpan) => {
  const [y, x, height] = gridSpan.split(',').map(Number);
  return { x, y, height };
};

export const getProductTypeFromMetadata = (metadata) => {
  const gridSpan = metadata?.GridSpan;
  const parsed = gridSpan ? parseGridSpan(gridSpan) : { x: 0, y: 0, height: 0 }; // fallback if gridSpan is undefined

  // Determine type based on dimensions
  if (parsed.x >= 4 && parsed.y >= 4) return 'full';
  if (parsed.x >= 4 || parsed.y >= 4) return 'wide';
  return 'narrow';
};

const getQuadrants = ({ x, y, w, d }) => {
  const quad = new Set();
  if (y < 2) {
    if (x < 2) quad.add('RL');
    if (x + w > 2) quad.add('RR');
  }
  if (y + d > 2) {
    if (x < 2) quad.add('FL');
    if (x + w > 2) quad.add('FR');
  }
  return Array.from(quad);
};

export const getHeightMap = (products, metadata) => {
  const map = { RL: 0, FL: 0, RR: 0, FR: 0 };

  products.forEach((product) => {
    const productMetadata = metadata?.[product.choiceId]?.metadata;
    const gridSpan = productMetadata?.GridSpan;
    if (!gridSpan) return;

    const { x: w, y: d, height } = parseGridSpan(gridSpan);

    const quads = getQuadrants({
      x: product.Origin?.[1],
      y: product.Origin?.[0],
      w,
      d
    });
    // whenever the height is -1, that area map[q] should be -1
    if (height === -1) {
      quads.forEach((q) => {
        map[q] = -1;
      });
      return; // skip adding height
    }
    quads.forEach((q) => {
      map[q] += height;
    });
  });

  return map;
};

const deriveBaseMode = (products, metadata) => {
  if (!products?.length) return null;
  const first = products[0];
  const span = parseGridSpan(metadata?.[first.choiceId]?.metadata?.GridSpan || '2,2,1');
  if (span.x >= 4 && span.y >= 4) return 'full';
  if (span.x >= 4 || span.y >= 4) return 'wide';
  return 'narrow';
};

export const calculate3DDropZones = (selectedProductType, products, metadata, isInitial) => {
  const zones = [];
  const heights = getHeightMap(products, metadata);
  if (!selectedProductType) return zones;

  // Determine if we should restrict (block) rear placements:
  // If there are existing products, base it on the first placed item's mode.
  // If none placed yet (isInitial), base it on the selected product being NOT full.
  let restrictToFront;
  if (products?.length) {
    const firstMode = deriveBaseMode(products, metadata); // narrow | wide | full
    restrictToFront = firstMode !== 'full';
  } else {
    restrictToFront = selectedProductType !== 'full';
  }

  // Narrow zones (individual quadrants)
  if (selectedProductType === 'narrow') {
    Object.keys(QUADRANTS).forEach((key) => {
      const pos = QUADRANTS[key];
      const z = heights[key];
      const isRear = key === 'RL' || key === 'RR';
      const zone = {
        ...pos,
        z,
        id: `dropzone-${key}-${z}`,
        type: 'narrow'
      };
      if ((restrictToFront && isRear) || z === -1) {
        zone.isBlocked = true;
      }
      zones.push(zone);
    });
  }

  // Wide zones (front spans FL+FR, rear spans RL+RR)
  if (selectedProductType === 'wide') {
    const frontHeight = heights.FL;
    const frontMismatch = heights.FL !== heights.FR;
    zones.push({
      x: 0,
      y: 2,
      z: frontHeight,
      id: `dropzone-wide-front-${frontHeight}`,
      type: 'wide',
      isBlocked: frontMismatch || frontHeight === -1
    });

    const rearHeight = heights.RL;
    const rearMismatch = heights.RL !== heights.RR;
    zones.push({
      x: 0,
      y: 0,
      z: rearHeight,
      id: `dropzone-wide-rear-${rearHeight}`,
      type: 'wide',
      isBlocked: restrictToFront || rearMismatch || rearHeight === -1
    });
  }

  // Full zone (single placement)
  if (selectedProductType === 'full') {
    const fullHeight = heights.FL;
    const allHeightsEqual = heights.FL === heights.FR
                           && heights.FR === heights.RR
                           && heights.RR === heights.RL;
    zones.push({
      x: 0,
      y: 0,
      z: fullHeight,
      id: `dropzone-full-${fullHeight}`,
      type: 'full',
      isBlocked: !allHeightsEqual || fullHeight === -1
    });
  }

  return zones;
};
